import { BoardsService } from './../../services/boards.service';
import { Component, Inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Board, BoardHeader, BoardStage } from '../../entities/board.entity';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { trigger, state, animate, transition, style } from '@angular/animations';
import { WebArticle } from '../../entities/web-article';

import { SubscriptionsManager } from '../../helpers/subscriptions-manager';
import { AppState, AppUserState } from '../../stores/states/app.state';
import { Store } from '@ngrx/store';
import { selectCurrentUser } from '../../stores/selectors/user.selectors';
import { NgxSpinnerService } from 'ngx-spinner';
import dayjs from 'dayjs';
import { HotToastService } from '@ngxpert/hot-toast';


export enum PinAction {
  ADD_TO_EXISTING = 'ADD-TO-EXISTING',
  ADD_NEW = 'ADD-NEW',
}
@Component({
  selector: 'app-pin-manager-modal',
  templateUrl: './pin-manager-modal.component.html',
  styleUrl: './pin-manager-modal.component.scss',
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideAnimation', [
      state('in', style({ transform: 'translateY(0)' })),
      state('out', style({ transform: 'translateY(100%)' })),
      transition('in => out', animate('1000ms ease-in-out')),
      transition('out => in', animate('1000ms ease-in-out'))
    ])
  ]
})
export class PinManagerModalComponent implements OnDestroy {
  currentUser?: AppUserState;
  newBoardTitle = '';
  PinAction = PinAction;

  action: PinAction | null = null;
  boards: BoardHeader[] = [];
  filteredBoards: BoardHeader[] = [];
  article!: WebArticle | any;
  calendarActivity!: any;
  onLoading = false;
  searchTerm: string = '';
  calendarActivityTypes: any[] = [];

  private subsManager = new SubscriptionsManager();



  constructor(
    private spinner: NgxSpinnerService,
    private store: Store<AppState>,
    private toast: HotToastService,
    private boardService: BoardsService,
    public dialogRef: MatDialogRef<PinManagerModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any) {
    if (data && data.boards) {

      this.boards = data.boards.filter((b: Board) => b.stage === BoardStage.DRAFT);
      this.filteredBoards = [...this.boards];

      this.article = data.article || undefined;
      this.calendarActivity = data.calendarActivity || undefined;
      if (this.calendarActivity) {
        this.calendarActivityTypes = data.calendarActivityTypes || [];
      }

      if (this.boards.length > 0) {
        this.action = PinAction.ADD_TO_EXISTING;
      } else {
        this.action = PinAction.ADD_NEW;
      }
    }

  }
  ngOnDestroy(): void {
    this.subsManager.unsubscribeAll();
  }
  checkIfExistInBoard(board: BoardHeader) {
    return (this.article && board.articleRef?.includes(this.article.documentNumber)) ||
      (this.calendarActivity && board.calendarActivitiesRef?.includes(this.calendarActivity.activityID));
  }

  ngOnInit() {

    this.subsManager.add(
      this.store.select(selectCurrentUser).subscribe(currentUser => {
        this.currentUser = currentUser;
      })
    );
  }
  replace(value: string) {
    return value.replace(' ', '-').toLowerCase();
  }

  closeModal(params: any = null) {
    this.dialogRef.close(params);
  }
  pinToBoard(boardUUID: string) {
    this.spinner.show();
    const pinData = {
      boardUUID: boardUUID,
      articles: this.article ? [this.article] : undefined,
      calendarActivities: this.calendarActivity ? [this.calendarActivity] : undefined
    };
    if (!this.article) {
      delete pinData?.articles;
    }
    if (!this.calendarActivity) {
      delete pinData?.calendarActivities;
    }
    this.boardService.pinToBoard(pinData).then((result) => {
      if (result.success) {
        this.closeModal(true);
      }
    }).catch(err => {
      console.error('Error creating board', err);
      this.toast.error('Error creating board, Try again later');
      this.closeModal(false);
    }).finally(() => {
      this.spinner.hide();
    });

  }
  addNew() {
    if (this.newBoardTitle.length && (this.article || this.calendarActivity) && this.currentUser) {
      this.spinner.show();
      this.boardService.createBoard({
        title: this.newBoardTitle,
        stage: BoardStage.DRAFT,
        owner: this.currentUser,
        articles: this.article ? [this.article] : [],
        calendarActivities: this.calendarActivity ? [this.calendarActivity] : []
      }).then((result) => {
        if (result.success) {
          this.toast.success(`Board ${this.newBoardTitle} added successfully`);
          this.closeModal(true);
        }
      }).catch(err => {
        console.error('Error creating board', err);
        this.toast.error('Error creating board, Try again later');
        this.closeModal(false);
      }).finally(() => {
        this.spinner.hide();
      });
    }
  }


  onSearch($event: any) {
    if (!$event.target.value.length) {
      return;
    }
    this.searchTerm = $event.target.value;

    if (this.searchTerm.length > 2) {
      this.filteredBoards = [...this.boards.filter(b => b.title.toLocaleLowerCase().includes(this.searchTerm.toLocaleLowerCase()))];
    } else {
      this.filteredBoards = [...this.boards];
    }
  }
  suggerestBoardTitle() {
    this.onLoading = true;
    this.newBoardTitle = '';
    const params = this.article ? { documentNumber: this.article.documentNumber } : { activityID: this.calendarActivity.activityID };
    this.boardService.generateTitle({ ...params, type: 'suggestion' }).then((data) => {
      if (data.success) {
        setTimeout(() => {
          this.newBoardTitle = data.suggested;
        }, 1000);

      }
    }).catch((err) => {
      console.error('error on suggerest board title', err);
    }).finally(() => {
      this.onLoading = false;
    });
  }

  getFormattedDateTime(unixDate: any): string {
    return dayjs.unix(unixDate).utc().format('MM/DD/YYYY');
  }

  private _cachedTitle: string | null = null;
  private _cachedActivityId: string | null = null;

  getActivityTitle(): string {

    const activityId = this.calendarActivity?.activityID || this.article?.documentNumber;
    if (this._cachedActivityId === activityId && this._cachedTitle !== null) {
      return this._cachedTitle;
    }

    if (this.article?.title) {
      this._cachedTitle = this.article.title;
      this._cachedActivityId = activityId;
      return this._cachedTitle as any;
    }

    if (this.calendarActivity) {

      switch (this.calendarActivity.type) {
        case 'senateCommunications':
          const senateCommTitle = `${this.calendarActivity.detail?.number || ''}: ${this.calendarActivity.detail?.abstract || ''}`;
          this._cachedTitle = senateCommTitle;
          this._cachedActivityId = activityId;
          return senateCommTitle;

        case 'amendments':
          const amendmentTitle = this.calendarActivity.detail?.amendedBill?.title ||
            this.calendarActivity.detail?.description ||
            this.calendarActivity.detail?.purpose || '';
          this._cachedTitle = amendmentTitle;
          this._cachedActivityId = activityId;
          return amendmentTitle;

        case 'bills':
          const billsTitle = this.calendarActivity.origin?.title || '';
          this._cachedTitle = billsTitle;
          this._cachedActivityId = activityId;
          return billsTitle;

        case 'summaries':
          const summariesTitle = this.calendarActivity.origin?.text || '';
          this._cachedTitle = summariesTitle;
          this._cachedActivityId = activityId;
          return summariesTitle;

        case 'hearings':
          const hearingsTitle = `${this.calendarActivity.detail?.libraryOfCongressIdentifier || ''}: ${this.calendarActivity.detail?.title || ''}`;
          this._cachedTitle = hearingsTitle;
          this._cachedActivityId = activityId;
          return hearingsTitle;

        case 'committeeMeetings':
          const committeeTitle = this.calendarActivity.detail?.title || '';
          this._cachedTitle = committeeTitle;
          this._cachedActivityId = activityId;
          return committeeTitle;

        case 'houseCommunications':
          const houseCommTitle = `${this.calendarActivity.detail?.number || ''}: ${this.calendarActivity.detail?.abstract || ''}`;
          this._cachedTitle = houseCommTitle;
          this._cachedActivityId = activityId;
          return houseCommTitle;

        case 'boundCongressionalRecord':
          const boundTitle = this.calendarActivity.detail?.title || this.calendarActivity.detail?.abstract || '';
          this._cachedTitle = boundTitle;
          this._cachedActivityId = activityId;
          return boundTitle;

        case 'treaties':
          const treatiesTitle = this.calendarActivity.detail?.title || this.calendarActivity.detail?.description || '';
          this._cachedTitle = treatiesTitle;
          this._cachedActivityId = activityId;
          return treatiesTitle;

        case 'HAC-D':
        case 'SAC-D':
          const hacdTitle = this.calendarActivity.detail?.title || this.calendarActivity.detail?.description || '';
          this._cachedTitle = hacdTitle;
          this._cachedActivityId = activityId;
          return hacdTitle;

        default:
          // Fallback: try to find any title-like field
          const fallbackTitle = this.calendarActivity.title ||
            this.calendarActivity.detail?.title ||
            this.calendarActivity.detail?.description ||
            this.calendarActivity.detail?.abstract ||
            this.calendarActivity.detail?.purpose ||
            this.calendarActivity.origin?.title ||
            this.calendarActivity.origin?.text ||
            '';
          this._cachedTitle = fallbackTitle;
          this._cachedActivityId = activityId;
          return fallbackTitle;
      }
    }

    this._cachedTitle = '';
    this._cachedActivityId = activityId;
    return '';
  }

  getActivityType(): string {
    if (this.calendarActivity?.type) {

      return this.calendarActivity.type
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str: string) => str.toUpperCase())
        .trim();
    }
    return '';
  }
}
